/**
 * Single source of truth: "future free cancellation" requires a real deadline strictly after `now`.
 * Refundable / Refundability without a parseable future deadline → not treated as future CXL.
 */

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const LOG_PREFIX = "[DYNAMIC_PACKAGE_FUTURE_CXL]";

function logGuard(level, message, data) {
  const fn = level === "error" ? console.error : console.warn;
  try {
    fn(`${LOG_PREFIX} ${message}`, data ?? "");
  } catch {
    /* ignore */
  }
}

/**
 * @param {unknown} raw
 * @returns {import("dayjs").Dayjs|null}
 */
export function parseCancellationDeadline(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const ms = raw > 1e12 ? raw : raw * 1000;
    const d = dayjs(ms);
    return d.isValid() ? d : null;
  }
  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s) return null;
    const dotnet = s.match(/^\/Date\((-?\d+)(?:[+-]\d{4})?\)\/$/);
    if (dotnet) {
      const d = dayjs(Number(dotnet[1]));
      return d.isValid() ? d : null;
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
      const d = dayjs(s, "DD/MM/YYYY", true);
      return d.isValid() ? d.endOf("day") : null;
    }
    const dIso = dayjs(s);
    if (dIso.isValid()) return dIso;
    const dLoose = dayjs(s, ["DD/MM/YYYY", "YYYY-MM-DD", "DD-MM-YYYY"], true);
    return dLoose.isValid() ? dLoose : null;
  }
  if (raw instanceof Date) {
    const d = dayjs(raw);
    return d.isValid() ? d : null;
  }
  return null;
}

/**
 * Gemonix-style: Refundability === 2 → non-refundable.
 * @param {unknown} v
 */
function isExplicitNonRefundable(v) {
  const n = Number(v);
  return Number.isFinite(n) && n === 2;
}

/**
 * Truthy refundability flag (camelCase from normalized profile) — still not enough without deadline.
 * @param {unknown} v
 */
function refundabilityFlagTruthy(v) {
  if (v === true) return true;
  if (v === false || v == null || v === "") return false;
  const n = Number(v);
  if (Number.isFinite(n)) return n !== 0 && n !== 2;
  if (typeof v === "string") {
    const t = v.trim().toLowerCase();
    return t === "true" || t === "1" || t === "yes";
  }
  return Boolean(v);
}

/**
 * Collect first non-empty deadline-like field from a policy-like object or nested shapes.
 * @param {unknown} policyLike
 * @returns {{ raw: unknown, path: string } | null}
 */
export function pickCancellationDeadlineRaw(policyLike) {
  if (!policyLike || typeof policyLike !== "object") return null;
  const o = policyLike;
  const keys = [
    ["RefundableUntil", "RefundableUntil"],
    ["refundableUntil", "refundableUntil"],
    ["freeCancellationUntil", "freeCancellationUntil"],
    ["FreeCancellationUntil", "FreeCancellationUntil"],
    ["cancellationDeadline", "cancellationDeadline"],
    ["CancellationDeadline", "CancellationDeadline"],
  ];
  for (const [k, path] of keys) {
    const v = o[k];
    if (v != null && v !== "") return { raw: v, path };
  }
  const policies = o.cancellationPolicies ?? o.CancellationPolicies;
  if (Array.isArray(policies) && policies.length > 0) {
    const p0 = policies[0];
    if (p0 && typeof p0 === "object") {
      for (const [k, path] of keys) {
        const v = p0[k];
        if (v != null && v !== "") return { raw: v, path: `cancellationPolicies[0].${path}` };
      }
      const u = p0.until ?? p0.Until ?? p0.deadline ?? p0.Deadline;
      if (u != null && u !== "") return { raw: u, path: "cancellationPolicies[0].until|deadline" };
    }
  }
  const textDateKeys = [
    ["CancellationPolicy", "CancellationPolicy"],
    ["cancellationPolicy", "cancellationPolicy"],
    ["cancellation", "cancellation"],
    ["Cancellation", "Cancellation"],
  ];
  for (const [k, path] of textDateKeys) {
    const v = o[k];
    if (typeof v === "string" && v.trim()) {
      const p = parseCancellationDeadline(v);
      if (p && p.isValid()) return { raw: v, path };
    }
  }
  return null;
}

function extractPackageRoomsArray(pkg) {
  if (!pkg || typeof pkg !== "object" || pkg.Rooms == null) return [];
  const r = pkg.Rooms;
  return Array.isArray(r) ? r : typeof r === "object" ? Object.values(r) : [];
}

/**
 * Gemonix: שדות ביטול לעיתים על Rooms[0] בלבד. שדות שקיימים על החבילה גוברים.
 * @param {unknown} policyLike
 */
export function normalizePolicyLikeForFutureCxl(policyLike) {
  if (!policyLike || typeof policyLike !== "object") return policyLike;
  const arr = extractPackageRoomsArray(policyLike);
  const r0 = arr[0];
  if (!r0 || typeof r0 !== "object") return policyLike;
  const CXL_KEYS = [
    "RefundableUntil",
    "refundableUntil",
    "Refundability",
    "refundability",
    "freeCancellationUntil",
    "FreeCancellationUntil",
    "cancellationDeadline",
    "CancellationDeadline",
    "cancellationPolicies",
    "CancellationPolicies",
    "CancellationPolicy",
    "cancellationPolicy",
    "cancellation",
    "Cancellation",
  ];
  const overlay = {};
  for (const k of CXL_KEYS) {
    const v = r0[k];
    if (v != null && v !== "") overlay[k] = v;
  }
  return { ...overlay, ...policyLike };
}

/**
 * @param {unknown} policyLike
 * @param {Date|number|string} [nowInput]
 * @returns {{
 *   hasFutureCxl: boolean,
 *   refundableUntil: string | null,
 *   deadlineMs: number | null,
 *   reason: string,
 *   rawDeadline: unknown,
 *   deadlineSourcePath: string | null,
 * }}
 */
export function hasFutureCancellationPolicy(policyLike, nowInput = undefined) {
  const now = dayjs(nowInput ?? new Date());
  const policyNorm = normalizePolicyLikeForFutureCxl(policyLike);
  if (!policyNorm || typeof policyNorm !== "object") {
    return {
      hasFutureCxl: false,
      refundableUntil: null,
      deadlineMs: null,
      reason: "no_policy",
      rawDeadline: null,
      deadlineSourcePath: null,
    };
  }

  const o = policyNorm;
  if (isExplicitNonRefundable(o.Refundability ?? o.refundability)) {
    return {
      hasFutureCxl: false,
      refundableUntil: null,
      deadlineMs: null,
      reason: "non_refundable_refundability_2",
      rawDeadline: null,
      deadlineSourcePath: null,
    };
  }

  const picked = pickCancellationDeadlineRaw(o);
  if (!picked) {
    const looksRefundable =
      refundabilityFlagTruthy(o.refundability) ||
      refundabilityFlagTruthy(o.Refundability) ||
      o.Refundability === 1 ||
      o.refundability === 1;
    return {
      hasFutureCxl: false,
      refundableUntil: null,
      deadlineMs: null,
      reason: looksRefundable ? "refundable_no_deadline" : "no_deadline_fields",
      rawDeadline: null,
      deadlineSourcePath: null,
    };
  }

  const parsed = parseCancellationDeadline(picked.raw);
  if (!parsed || !parsed.isValid()) {
    return {
      hasFutureCxl: false,
      refundableUntil: null,
      deadlineMs: null,
      reason: "unparseable_deadline",
      rawDeadline: picked.raw,
      deadlineSourcePath: picked.path,
    };
  }

  const deadlineMs = parsed.valueOf();
  if (!parsed.isAfter(now)) {
    return {
      hasFutureCxl: false,
      refundableUntil: parsed.toISOString(),
      deadlineMs,
      reason: "deadline_parsed_past",
      rawDeadline: picked.raw,
      deadlineSourcePath: picked.path,
    };
  }

  return {
    hasFutureCxl: true,
    refundableUntil: parsed.toISOString(),
    deadlineMs,
    reason: "deadline_future",
    rawDeadline: picked.raw,
    deadlineSourcePath: picked.path,
  };
}

/**
 * @param {unknown[]} packages
 * @param {Date} [now]
 * @returns {unknown[]}
 */
export function filterPackagesToFutureCxlOnly(packages, now = new Date()) {
  if (!Array.isArray(packages)) return [];
  const out = [];
  for (const pkg of packages) {
    if (hasFutureCancellationPolicy(pkg, now).hasFutureCxl) out.push(pkg);
  }
  return out;
}

/**
 * @param {unknown[]} rooms — hotel profile room groups with `.packages[]`
 * @param {Date} [now]
 * @returns {unknown[]}
 */
export function filterDynamicPackageRoomGroupsToFutureCxlOnly(rooms, now = new Date()) {
  if (!Array.isArray(rooms)) return [];
  const out = [];
  for (const room of rooms) {
    if (!room || typeof room !== "object") continue;
    const pkgs = room.packages;
    if (!Array.isArray(pkgs) || pkgs.length === 0) continue;
    const kept = filterPackagesToFutureCxlOnly(pkgs, now);
    if (kept.length === 0) continue;
    out.push({ ...room, packages: kept });
  }
  return out;
}

export { LOG_PREFIX as DYNAMIC_PACKAGE_FUTURE_CXL_LOG_PREFIX, logGuard as logDynamicPackageFutureCxlGuard };
